import { lazy, Suspense, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { env } from '@/lib/env'
import { CdbMark } from './CdbMark'
import './login.css'

const BrainPanel = lazy(() => import('./BrainPanel'))
const EnergyLinesPanel = lazy(() => import('./EnergyLinesPanel'))

export type LoginVariant = 'cdb' | 'siemens'

/** cdbos.de by default; a Siemens build (VITE_APP_BRANDING=siemens) gets its own look and copy. */
export function loginVariant(): LoginVariant {
  return env.appBranding === 'siemens' ? 'siemens' : 'cdb'
}

const CONTACT_MAIL = 'hello@cdbrain.de'

interface LoginShellProps {
  /** The sign-in card: one button and one hint line. */
  card: ReactNode
  /** Waiting-room or refusal message, shown below the card so the card never changes size. */
  status?: ReactNode
}

/**
 * Split sign-in screen (cdb_webos#1529): sign-in column left, brand panel right.
 * The layout is shared; the variant switches palette, claim and panel visual.
 */
export function LoginShell({ card, status }: LoginShellProps) {
  const { t, i18n } = useTranslation()
  const variant = loginVariant()
  const siemens = variant === 'siemens'
  const lang = i18n.language?.startsWith('en') ? 'en' : 'de'
  const copy = siemens ? 'auth.loginPage.siemens' : 'auth.loginPage'
  const mailto = `mailto:${CONTACT_MAIL}?subject=${encodeURIComponent(t('auth.loginPage.mailSubject'))}`

  return (
    <div className="login-screen" data-variant={variant} data-testid="login-screen">
      <section className="login-side">
        <header className="login-top">
          {siemens ? (
            <img className="login-logo" src="/assets/branding/siemens/logo.png" alt="Siemens" />
          ) : (
            <span className="login-brand" aria-label="Corporate Digital Brain">
              <CdbMark />
              <span className="login-brand-word" aria-hidden="true">Corporate<em>Digital Brain</em></span>
            </span>
          )}
          <div className="login-lang" role="group" aria-label={t('common.changeLanguage')}>
            <button type="button" aria-pressed={lang === 'de'} onClick={() => i18n.changeLanguage('de')}>DE</button>
            <span aria-hidden="true">·</span>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => i18n.changeLanguage('en')}>EN</button>
          </div>
        </header>

        <div className="login-center">
          <div className="login-stack">
            <div className="login-claim">
              {siemens && <p className="login-eyebrow">{t('auth.loginPage.siemens.eyebrow')}</p>}
              <h1>
                {t(`${copy}.claimLead`)} <em>{t(`${copy}.claimAccent`)}</em>
              </h1>
              <p>{t(`${copy}.claimSub`)}</p>
            </div>

            <div className="login-card">{card}</div>

            {status && <div aria-live="polite">{status}</div>}

            {!siemens && (
              <p className="login-contact">
                {t('auth.loginPage.noAccount')} <a href={mailto}>{t('auth.loginPage.contactUs')}</a>
                <span className="login-contact-mail">{CONTACT_MAIL}</span>
              </p>
            )}
          </div>
        </div>

        {siemens && (
          <footer className="login-foot">
            <CdbMark />
            <span>{t('auth.loginPage.siemens.poweredBy')}</span>
          </footer>
        )}
      </section>

      <aside className="login-panel" aria-hidden="true" data-testid="login-panel">
        <Suspense fallback={null}>{siemens ? <EnergyLinesPanel /> : <BrainPanel />}</Suspense>
      </aside>
    </div>
  )
}
